import React, { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { resolveHref } from 'next/dist/client/resolve-href';
import { useQuery } from '@tanstack/react-query';
import { BuddyApi } from 'src/api';
import { Stack } from 'src/components';
import HeaderDropdownIcon from 'src/components/HeaderDropdownIcon';
import { useUserContext } from 'src/context/user';
import { DropdownSectionProps } from 'src/components/DropdownMenu';
import { ZENDESK } from 'src/constants/links.const';
import { ROUTES } from 'src/constants/routes.const';
import { ONE_MINUTE } from 'src/constants/times.const';
import { PlatformPage } from 'src/utils/analytics/analytics.const';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { getAnalyticsLanguageInfo } from 'src/utils/analytics/getAnalyticsLanguageInfo';
import { getAnalyticsUserInfo } from 'src/utils/analytics/getAnalyticsUserInfo';
import * as S from '../styles';

const msgs = defineMessages({
  profileTooltip: { id: 'dashboard_tooltip_profile_dropdown' },
  scenariosProfileTooltip: { id: 'scenario_dashboard_tooltip_profile_dropdown' },
  yourAccount: { id: 'scenario_dashboard_tooltip_profile_dropdown_your_account' },
  learningSettings: { id: 'web_ls_menu' },
  profileSettings: { id: 'web_ls_menu_profile' },
  logOut: { id: 'sign_out' },
  help: { id: 'header_link_help' },
});

export const ProfileDropdown: FC<{ isSupportedLanguagePairBuddies?: boolean }> = ({
  isSupportedLanguagePairBuddies,
}) => {
  const user = useUserContext();
  const intl = useIntl();
  const languagePairId = getLanguagePairId();
  const router = useRouter();

  const { data: buddiesData } = useQuery({
    queryKey: ['buddiesWithCache', languagePairId],
    queryFn: () => BuddyApi.getAll(languagePairId as number),
    enabled: isSupportedLanguagePairBuddies && !!languagePairId,
    staleTime: 5 * ONE_MINUTE,
  });
  const assistantBuddy = buddiesData?.buddies.find(b => b.id === 'assistant');

  const profileSections: DropdownSectionProps[] = [
    {
      dropdownItems: [
        {
          type: 'link',
          linkText: intl.formatMessage(msgs.profileSettings),
          href: ROUTES.settings.user,
          onClick: () => {
            window.dataLayer.push({
              event: 'platform',
              page_screen_type: 'Platform',
              page_screen_name: PlatformPage.PROFILE,
              ...getAnalyticsLanguageInfo(intl),
              ...getAnalyticsUserInfo(user?.user),
            });
          },
        },
        ...(!languagePairId
          ? []
          : [
              {
                type: 'link' as const,
                linkText: intl.formatMessage(msgs.learningSettings),
                href: ROUTES.settings.learning,
              },
            ]),
      ],
    },
    {
      dropdownItems: [
        {
          type: 'link',
          newTab: !assistantBuddy,
          linkText: intl.formatMessage(msgs.help),
          href: assistantBuddy
            ? resolveHref(router, {
                pathname: ROUTES.ai.dialog,
                query: { type: assistantBuddy.id },
              })
            : ZENDESK,
        },
      ],
    },
    {
      dropdownItems: [
        {
          type: 'link',
          linkText: intl.formatMessage(msgs.logOut),
          highlight: true,
          href: ROUTES.signOut,
        },
      ],
    },
  ];

  const profileIconText = intl.formatMessage(msgs.yourAccount);

  return (
    <Stack $direction="horizontal" $spacing="s8">
      <HeaderDropdownIcon
        dropdownSections={profileSections}
        tooltipText={intl.formatMessage(msgs.scenariosProfileTooltip, {
          yourAccount: <strong key="strong">{profileIconText}</strong>,
          email: <S.NoWrap key="NoWrap">{user.user?.email}</S.NoWrap>,
        })}
        isPremium={!!user.user?.isPro}
        ariaText={profileIconText}
      />
    </Stack>
  );
};
